import React from "react";
import glamorous from "glamorous";
import moment from "moment";
import _ from "lodash";
import HeaderIcon from "../../assets/icons/reverse-field-map-droplet.svg";

import { getDistance, getStopLatLong } from "../map/RoutingMapView";

import Colors, { shadeColor } from "../../styles/colors";
import {
  LadChicletCSS,
  MadChicletCSS,
  colorForLad
} from "../../components/chiclets";
import { textColorForExceptionType } from "../exceptions/ExceptionLink";

import ShipmentDetailTabs from "./ShipmentDetailTabs";
import { colorForExceptionType } from "../exceptions/ExceptionLink";
import { ScheduledArrivalWindow } from "./ArrivalWindow";

// DEV-256: note that we used to select origin_earliest_arrival and
// destination_earliest_arrival from the shipment.  The ask here is to
// use the "latest" arrival values.  We have not renamed the fields, so
// earliest_arrival_datetime gets the later value.

const getLocationDataFromShipment = shipment => {
  const {
    origin_name,
    destination_name,
    origin_earliest_arrival,
    destination_earliest_arrival,
    origin_actual_arrival,
    destination_actual_arrival,
    origin_latest_arrival,
    destination_latest_arrival,
    origin_city,
    origin_state,
    origin_postal_code,
    destination_city,
    destination_state,
    destination_postal_code,
    origin_calculated_eta,
    destination_calculated_eta
  } = shipment;

  const origin = {
    name: origin_name,
    earliest_arrival_datetime: origin_earliest_arrival,
    latest_arrival_datetime: origin_latest_arrival,
    actual_arrival_datetime: origin_actual_arrival,
    city: origin_city,
    state: origin_state,
    postal_code: origin_postal_code,
    calculated_eta: origin_calculated_eta
  };
  const destination = {
    name: destination_name,
    earliest_arrival_datetime: destination_earliest_arrival,
    latest_arrival_datetime: destination_latest_arrival,
    actual_arrival_datetime: destination_actual_arrival,
    city: destination_city,
    state: destination_state,
    postal_code: destination_postal_code,
    calculated_eta: destination_calculated_eta
  };

  return {
    origin,
    destination
  };
};

function computeStopSpacing(stops) {
  var totalDistance = 0.0;
  var stopDistances = [];
  var lastPos = null;

  // Start by finding the distance between
  // each of the stops.  The JSON back from the
  // API does have a distance field, but it
  // looks to be invalid, so recompute it
  // here based on math distance, so not perfect
  // but close enough for our UI display
  stops.forEach(s => {
    var pos = getStopLatLong(s);

    var d = 0.0;
    if (lastPos !== null) d = getDistance(pos, lastPos);

    lastPos = pos;
    totalDistance += d;
    stopDistances.push(totalDistance);
  });

  // The default stop spacing is just a percentage
  // of the way along the route for that shipment
  var stopSpacing = [];
  stopDistances.forEach(d => {
    stopSpacing.push((d / totalDistance) * 100.0);
  });

  // This function is used to adjust inner
  // spacing of the stops.  If the percentages
  // are too close, the displayed LADs will overlap
  // so for UI purposes, try to adjust the spacing
  // This will slide things down to left in an
  // accordian fashion.
  function adjustStopSpacing() {
    const minSpacing = 11.0;
    var adjustedSpacing = false;

    for (var i = stopSpacing.length - 2; i > 1; i--) {
      const diff = stopSpacing[i] - stopSpacing[i - 1];

      // If at least the min spacing requirement
      // skip this one
      if (diff >= minSpacing) {
        continue;
      }

      // If the diff is near zero it is likely the same
      // location, let it draw exactly on itself
      if (diff < 0.5) {
        continue;
      }

      adjustedSpacing = true;

      // Let's see if we should move this shipment to the left
      // or to the right
      const leftSpace = stopSpacing[i - 1] - stopSpacing[i - 2];
      const rightSpace = stopSpacing[i + 1] - stopSpacing[i];

      if (rightSpace > leftSpace && rightSpace > minSpacing) {
        stopSpacing[i] = stopSpacing[i - 1] + minSpacing;
      } else {
        // At this point, slide the i-1 stop over
        // a bit, but don't pass the i-2 stop
        stopSpacing[i - 1] = _.max([
          stopSpacing[i - 2] + 1.0,
          stopSpacing[i] - minSpacing
        ]);
      }
    }
    return !adjustedSpacing;
  }

  // If we have at least three stops, check if
  // any of them need to be adjusted
  if (stopSpacing.length > 2) {
    var validSpacing = false;
    var tryCount = 0;
    while (!validSpacing && tryCount < 5) {
      validSpacing = adjustStopSpacing();
      tryCount += 1;
    }
  }

  // Looks like a rounding error for placement
  // of the last tick mark for the final destination
  // So bring it in a smidge
  if (stopSpacing.length > 1) {
    stopSpacing[stopSpacing.length - 1] = 99.5;
  }

  return stopSpacing;
}

const DetailItem = props => {
  const { label, style } = props;

  return (
    <glamorous.Div
      css={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        marginRight: "1em",
        ...style
      }}
    >
      <span style={{ color: "#999", marginBottom: "5px", fontSize: "normal" }}>
        {label}
      </span>
      <span style={{ color: Colors.background.DARK_BLUE, fontSize: "1.25em" }}>
        {props.children}
      </span>
    </glamorous.Div>
  );
};

const LocationBox = props => {
  const { isOrigin, loc, showETA } = props;

  const hasArrived = loc.actual_arrival_datetime !== null;

  var expectedActualTime;

  if (hasArrived) {
    expectedActualTime = loc.actual_arrival_datetime;
  } else {
    if (showETA) {
      expectedActualTime = loc.calculated_eta;
    } else {
      expectedActualTime = null;
    }
  }

  const timeFont = hasArrived
    ? { fontWeight: "bold", fontSize: "smaller" }
    : { fontStyle: "italic", fontSize: "smaller" };

  return (
    <glamorous.Div
      css={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        padding: "1em"
      }}
    >
      <DetailItem label={isOrigin ? "Origin" : "Destination"}>
        <glamorous.Div css={{ fontWeight: "bold", fontSize: "smaller" }}>
          {loc && loc.name}
        </glamorous.Div>
        <glamorous.Div css={{ fontSize: "smaller" }}>
          {loc && loc.city && `${loc.city}, ${loc.state} ${loc.postal_code}`}
        </glamorous.Div>
      </DetailItem>
      <glamorous.Div
        css={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          marginTop: "1em"
        }}
      >
        <DetailItem label="Scheduled" style={{ marginRight: "1em" }}>
          <ScheduledArrivalWindow loc={loc} />
        </DetailItem>
        <DetailItem
          label={hasArrived || showETA === false ? "Actual" : "Estimated"}
        >
          <glamorous.Div css={timeFont}>
            {expectedActualTime != null
              ? moment
                  .utc(expectedActualTime)
                  .local()
                  .format("HH:mm")
              : "N/A"}
          </glamorous.Div>
          <glamorous.Div css={{ fontSize: "smaller" }}>
            {expectedActualTime &&
              moment
                .utc(expectedActualTime)
                .local()
                .format("l")}
          </glamorous.Div>
        </DetailItem>
      </glamorous.Div>
    </glamorous.Div>
  );
};

const OriginDestinationBox = props => {
  const { shipment } = props;
  const { origin, destination } = getLocationDataFromShipment(shipment);

  // If any of our stops have an arrived at time
  // we will not populate the origin's ETA
  var showETA = true;
  if ("shipment_stops" in shipment && shipment.shipment_stops) {
    shipment.shipment_stops.forEach(stop => {
      if (stop.arrived_at != null) {
        showETA = false;
      }
    });
  }

  return (
    <glamorous.Div
      css={{
        display: "flex",
        flex: 1,
        justifyContent: "space-between",
        backgroundColor: "white",
        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.05)",
        borderRadius: "3px",
        margin: "1em"
      }}
    >
      <LocationBox isOrigin={true} showETA={showETA} loc={origin} />
      <glamorous.Div
        css={{
          width: "2px",
          backgroundColor: "#ddd",
          marginTop: "1em",
          marginBottom: "1em"
        }}
      />
      <LocationBox isOrigin={false} showETA={true} loc={destination} />
    </glamorous.Div>
  );
};

const ShipmentTimeline = props => {
  const { shipment } = props;

  if (shipment.shipment_stops == null) {
    return null;
  }

  // there is a stop distance field in the backend but doesn't look valid
  const stopSpacing = computeStopSpacing(shipment.shipment_stops);

  const stops = shipment.shipment_stops.map((s, i) => {
    const lad =
      s.location != null
        ? s.location.lad
        : {
            default_name: "Unclassified",
            lob_name: "Unclassified",
            description: "Unclassified",
            code: "U",
            id: 0
          };

    return (
      <div key={i}>
        <glamorous.Div
          css={{
            position: "absolute",
            left: `${stopSpacing[i]}%`
          }}
        >
          <LadChicletCSS lad={lad} width={40} height={40} showLadLabel />
        </glamorous.Div>
        <glamorous.Div
          css={{
            position: "absolute",
            width: 2,
            height: 33,
            backgroundColor: colorForLad(lad),
            left: `${stopSpacing[i]}%`,
            bottom: -72,
            marginLeft: 19,
            zIndex: 0
          }}
        />
      </div>
    );
  });

  // If we have a valid value for remaining distance, calculate the progress
  // otherwise, zero it out

  const progStr = shipment.current_location
    ? shipment.current_location.distance_progress.replace("%", "")
    : "0";
  const progress = _.max([0.0, Number(progStr)]);

  return (
    <glamorous.Div
      css={{
        position: "relative",
        display: "flex",
        margin: "1em",
        marginBottom: "5em",
        justifyContent: "space-between",
        height: 35
      }}
    >
      <glamorous.Div
        css={{
          zIndex: 1,
          display: "flex",
          flex: 1,
          justifyContent: "space-between"
        }}
      >
        <glamorous.Div
          css={{ position: "absolute", width: "calc(100% - 38px)", left: 0 }}
        >
          <glamorous.Div
            css={{
              position: "absolute",
              height: 25,
              backgroundColor: shadeColor(
                shipment.active_exceptions_ng
                  ? colorForExceptionType(shipment.active_exceptions_ng)
                  : Colors.highlight.GREEN,
                0.5
              ),
              left: 19,
              top: 47,
              right: `${100 - progress}%`,
              zIndex: 0
            }}
          />
          <glamorous.Div
            css={{
              position: "absolute",
              left: `${progress}%`,
              top: 42.5,
              zIndex: 1
            }}
          >
            <MadChicletCSS
              shipmentMode={shipment.mode_name}
              activeException={shipment.active_exceptions_ng}
              width={35}
              height={35}
            />
            <glamorous.Div
              css={{ color: Colors.background.DARK_BLUE, textAlign: "center" }}
            >
              {`${progress.toFixed(0)}%`}
            </glamorous.Div>
          </glamorous.Div>
          {stops}
        </glamorous.Div>
      </glamorous.Div>
      <glamorous.Div
        css={{
          position: "absolute",
          height: 27,
          backgroundColor: Colors.background.GRAY,
          marginTop: 46,
          left: 19,
          right: 19,
          zIndex: 0,
          border: "1px solid #999"
        }}
      />
    </glamorous.Div>
  );
};

const ShipmentDetailPanelHeader = props => {
  return (
    <glamorous.Div
      color={Colors.tableHeadingText}
      backgroundColor={Colors.tableHeadingBackground}
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      alignItems="baseline"
      padding="0.5em 0.5em 0.25em 0.5em"
    >
      <glamorous.Div display="flex" flexDirection="row" alignItems="baseline">
        <glamorous.Div>
          <img src={HeaderIcon} height="25px" width="25px" alt="icon" />
        </glamorous.Div>
        <glamorous.H3 fontSize="1.2em" margin="0" padding="0">
          Shipment Detail
        </glamorous.H3>
      </glamorous.Div>
    </glamorous.Div>
  );
};

const StatusLozengeStyles = {
  borderWidth: "2px",
  borderStyle: "solid",
  borderColor: "#eee",
  borderRadius: "4px",
  padding: "0 0.5em"
};

const getEquipNumber = references => {
  if (!references || _.isEmpty(references)) return "";

  const equipNum = references
    .filter(r => _.includes(["equipment_number"], r.qualifier))
    .map((r, i) => r.value);

  return _.head(equipNum) || "";
};

const ShipmentDetailPanel = ({ shipment, ladsList }) => {
  const exception = shipment.active_exceptions_ng;
  const exceptionText = exception || "N/A";
  const exceptionColor = exception
    ? textColorForExceptionType(exceptionText)
    : Colors.highlight.GREEN;
  const exceptionOutlineStyles = {
    ...StatusLozengeStyles,
    borderColor: exceptionColor
  };

  const mode = shipment.mode_name || "";

  return (
    <glamorous.Div
      css={{
        flexDirection: "column",
        backgroundColor: Colors.background.LIGHT_GRAY,
        height: "100em",
        overflow: "auto"
      }}
    >
      <ShipmentDetailPanelHeader />
      <glamorous.Div
        css={{
          display: "flex",
          flexDirection: "row",
          backgroundColor: "white",
          boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.1)",
          padding: "1em"
        }}
      >
        <DetailItem label="Shipment ID">
          {shipment.creator_shipment_id}
        </DetailItem>
        <DetailItem label="Asset ID">{shipment.obc_asset_id}</DetailItem>
        <DetailItem label="Mode">{shipment.mode_name}</DetailItem>
        <DetailItem label="Status">{shipment.current_status_ng}</DetailItem>
        <DetailItem label="Active Exceptions" style={exceptionOutlineStyles}>
          {/*
            note: despite "active exceptions" being plural above, what's in the
            shipment record appears to be a single bare string. So our display
            component only handles a single exception.
          */}
          <glamorous.Span color={exceptionColor}>
            {exceptionText}
          </glamorous.Span>
        </DetailItem>
      </glamorous.Div>
      <glamorous.Div
        css={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between"
        }}
      >
        <glamorous.Div
          css={{
            display: "flex",
            flexDirection: "column",
            margin: "1em",
            minWidth: "8em"
          }}
        >
          <Label css={{ marginTop: "1em" }}>Shipper</Label>
          <DetailField>{shipment.shipper_name}</DetailField>

          <Label>Carrier</Label>
          <DetailField>{shipment.carrier_name}</DetailField>

          <Label>SCAC</Label>
          <DetailField>{shipment.carrier_scac}</DetailField>

          <Label>Trailer #</Label>
          <DetailField>
            {mode === "Rail"
              ? getEquipNumber(shipment.shipment_references)
              : shipment.trailer_number}
          </DetailField>
        </glamorous.Div>
        <OriginDestinationBox shipment={shipment} />
      </glamorous.Div>
      <ShipmentTimeline shipment={shipment} ladsList={ladsList} />
      <ShipmentDetailTabs shipment={shipment} />
    </glamorous.Div>
  );
};

const Label = glamorous.h4({
  fontSize: "1em",
  color: Colors.text.GRAY,
  margin: "5px 0"
});
const DetailField = glamorous.p({
  fontSize: "1.1em"
});

export default ShipmentDetailPanel;



// WEBPACK FOOTER //
// ./src/modules/shipment-detail/ShipmentDetailPanel.js